import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Download, Users as UsersIcon, ShieldCheck, Lock, Ban } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, Pager, Toolbar, SearchBox, Select, RefreshButton, Status, Tag, Stat } from '../ui'
import { get, qs } from '../../lib/api'
import { clsx, compact, fa, faDate, num } from '../../lib/format'

export default function UsersPage() {
  const nav = useNavigate()
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [f, setF] = useState({ q: '', kyc: '', status: '', role: '', sort: 'recent', page: 1 })

  const load = () => {
    setLoading(true)
    get(`/admin/users${qs({ ...f, per_page: 25 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    const id = setTimeout(load, f.q ? 350 : 0)
    return () => clearTimeout(id)
  }, [f])

  const exportCsv = async () => {
    const r: any = await get('/admin/users/export')
    const url = URL.createObjectURL(new Blob(['﻿' + r.content], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url; a.download = r.filename; a.click()
    URL.revokeObjectURL(url)
  }

  const set = (patch: any) => setF((s) => ({ ...s, ...patch, page: patch.page ?? 1 }))

  return (
    <>
      <PageHead title="مدیریت کاربران" subtitle="جستجو، بررسی و مدیریت تمام حساب‌های کاربری"
        action={
          <div className="flex gap-2">
            <button onClick={exportCsv} className="btn-ghost !py-2 text-xs">
              <Download className="h-3.5 w-3.5" /> خروجی CSV
            </button>
            <RefreshButton onClick={load} loading={loading} />
          </div>
        } />

      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="کل کاربران" value={fa(meta?.total ?? 0)} icon={<UsersIcon className="h-5 w-5" />} />
        <Stat label="نمایش صفحه" value={fa(rows?.length ?? 0)} color="#22D3EE" />
        <Stat label="فیلتر فعال"
          value={[f.q && 'جستجو', f.kyc !== '' && 'سطح', f.status && 'وضعیت', f.role && 'نقش']
            .filter(Boolean).length || '—'} color="#FFB020" />
        <Stat label="مرتب‌سازی" value={{ recent: 'جدیدترین', oldest: 'قدیمی‌ترین', kyc: 'سطح احراز', email: 'ایمیل' }[f.sort]}
          color="#00E39A" />
      </div>

      <Panel dense>
        <div className="p-4 pb-0">
          <Toolbar>
            <SearchBox value={f.q} onChange={(q) => set({ q })} placeholder="ایمیل، موبایل، نام، کد ملی یا کد معرف…" />
            <Select value={f.kyc} onChange={(kyc) => set({ kyc })}
              options={[['', 'همه سطوح'], ['0', 'سطح ۰'], ['1', 'سطح ۱'], ['2', 'سطح ۲'], ['3', 'سطح ۳']]} />
            <Select value={f.status} onChange={(status) => set({ status })}
              options={[['', 'همه وضعیت‌ها'], ['active', 'فعال'], ['blocked', 'مسدود'], ['locked', 'محدودشده']]} />
            <Select value={f.role} onChange={(role) => set({ role })}
              options={[['', 'همه نقش‌ها'], ['user', 'کاربر'], ['support', 'پشتیبانی'],
                        ['finance', 'مالی'], ['admin', 'مدیر'], ['owner', 'مالک']]} />
            <Select value={f.sort} onChange={(sort) => set({ sort })}
              options={[['recent', 'جدیدترین'], ['oldest', 'قدیمی‌ترین'], ['kyc', 'سطح احراز'], ['email', 'ایمیل']]} />
          </Toolbar>
        </div>

        <DataTable
          rows={rows}
          loading={loading}
          minWidth={900}
          onRowClick={(r: any) => nav(`/admin/users/${r.id}`)}
          empty="کاربری با این مشخصات پیدا نشد"
          columns={[
            { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
            {
              key: 'user', header: 'کاربر', cell: (r: any) => (
                <div>
                  <div className="num text-slate-100" dir="ltr">{r.email}</div>
                  <div className="text-[11px] text-slate-500">{r.fullName || 'بدون نام'} · {r.mobile || '—'}</div>
                </div>
              ),
            },
            {
              key: 'kyc', header: 'احراز هویت',
              cell: (r: any) => <Tag kind={r.kycLevel > 0 ? 'info' : 'muted'}>سطح {fa(r.kycLevel)}</Tag>,
            },
            {
              key: 'role', header: 'نقش',
              cell: (r: any) => <Tag kind={r.role === 'user' ? 'muted' : 'gold'}>{r.roleLabel}</Tag>,
            },
            {
              key: 'balance', header: 'دارایی (تومان)',
              cell: (r: any) => <span className="num text-slate-200">{compact(r.balanceIrt)}</span>,
            },
            {
              key: 'flags', header: 'وضعیت', cell: (r: any) => (
                <div className="flex flex-wrap gap-1">
                  {r.isActive ? <Tag kind="up">فعال</Tag> : <Tag kind="down"><Ban className="ml-1 h-3 w-3" />مسدود</Tag>}
                  {r.tradeLocked && <Tag kind="gold">قفل معامله</Tag>}
                  {r.withdrawLocked && <Tag kind="gold">قفل برداشت</Tag>}
                  {r.twoFa && <Tag kind="info"><ShieldCheck className="ml-1 h-3 w-3" />۲FA</Tag>}
                </div>
              ),
            },
            {
              key: 'created', header: 'عضویت',
              cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt, false)}</span>,
            },
            {
              key: 'login', header: 'آخرین ورود',
              cell: (r: any) => <span className="text-slate-500">{r.lastLoginAt ? faDate(r.lastLoginAt, false) : '—'}</span>,
            },
          ]}
        />
        <Pager meta={meta} onPage={(page) => setF((s) => ({ ...s, page }))} />
      </Panel>
    </>
  )
}
